import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Users, Mail, Plus, ChevronDown, ChevronUp, Search, UserPlus, X } from 'lucide-react';
import AdminFeedback from './admin/AdminFeedback';
import AdminRevenue from './admin/AdminRevenue';
import AdminPractices from './admin/AdminPractices';
import AdminAuditLog from './admin/AdminAuditLog';
import AdminBreakGlass from './admin/AdminBreakGlass';
import { AdminStatsRow } from './admin/AdminStatsRow';
import { AdminExpandedSections } from './admin/AdminExpandedSections';
import { AdminUserRow } from './admin/AdminUserRow';
import { formatDate } from './admin/adminHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [extendingUser, setExtendingUser] = useState(null);
  const [extendDays, setExtendDays] = useState(30);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [editingReferralSettings, setEditingReferralSettings] = useState(null);
  const [referralMaxInvites, setReferralMaxInvites] = useState(6);
  const [referralRewardDays, setReferralRewardDays] = useState(30);
  const [adminRole, setAdminRole] = useState('admin');
  const [expandedSection, setExpandedSection] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [collapsedSections, setCollapsedSections] = useState({ users: true });
  const [activeTab, setActiveTab] = useState('overview');

  const isSuperAdmin = adminRole === 'superadmin';
  const toggleSection = (key) => setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    if (user && !['admin', 'superadmin'].includes(user.role)) {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, invitesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users`),
        axios.get(`${API_URL}/api/admin/beta-invites`)
      ]);
      setUsers(usersRes.data.users || []);
      setInvites(invitesRes.data.invites || []);
      if (usersRes.data.admin_role) setAdminRole(usersRes.data.admin_role);

      try {
        const fbRes = await axios.get(`${API_URL}/api/feedback`);
        setFeedbackItems(fbRes.data.feedback || []);
      } catch (err) {
        // silent
      }

      if (usersRes.data.admin_role === 'superadmin') {
        try {
          const revRes = await axios.get(`${API_URL}/api/admin/revenue`);
          setRevenue(revRes.data);
        } catch (err) {
          // silent
        }
      }
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/admin/beta-invite`, {
        email: inviteEmail.trim().toLowerCase(),
        name: inviteName.trim() || null
      });
      toast.success(`Beta invite sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteName('');
      setShowInviteForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevokeInvite = async (email) => {
    try {
      await axios.delete(`${API_URL}/api/admin/beta-invite/${encodeURIComponent(email)}`);
      toast.success(`Invite for ${email} revoked`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke invite');
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}`, updates);
      toast.success('User updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleExtendTrial = async (userId, reduce = false) => {
    const numDays = Math.abs(parseInt(extendDays, 10));
    if (!numDays || numDays < 1 || numDays > 365) {
      toast.error('Enter a number of days between 1 and 365, then click + Add or - Reduce');
      return;
    }
    try {
      const days = reduce ? -numDays : numDays;
      const res = await axios.put(`${API_URL}/api/admin/users/${userId}/extend-trial`, { days });
      toast.success(res.data.message);
      setExtendingUser(null);
      setExtendDays(30);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust trial');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const res = await axios.delete(`${API_URL}/api/admin/users/${userId}`);
      toast.success(res.data.message);
      setDeletingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleSaveReferralSettings = async (userId) => {
    try {
      const res = await axios.put(`${API_URL}/api/admin/users/${userId}/referral-settings`, {
        max_referrals: parseInt(referralMaxInvites),
        referral_reward_days: parseInt(referralRewardDays)
      });
      toast.success(res.data.message);
      setEditingReferralSettings(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update referral settings');
    }
  };

  const handleSendYearendEmails = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/send-yearend-emails`);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    }
  };

  const canModifyUser = (targetUser) => {
    if (isSuperAdmin) return targetUser.role !== 'superadmin' || targetUser.id !== user?.id;
    if (adminRole === 'admin') return targetUser.role === 'user';
    return false;
  };

  const startExtend = (userId) => { setExtendingUser(userId); setExtendDays(30); };

  if (!user || !['admin', 'superadmin'].includes(user.role)) return null;

  const pendingInvites = invites.filter(i => !i.used);
  const realUsers = users.filter(u => !u.email.includes('@test.com') && !u.email.includes('test@'));
  const activeTrialUsers = realUsers.filter(u => u.trial_active && u.is_trial);
  const subscribedUsers = realUsers.filter(u => u.tier === 'standard' || u.tier === 'premium');

  const filteredUsers = searchQuery.trim()
    ? realUsers.filter(u =>
        u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : realUsers;

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-white">Admin Panel</h1>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Admin Panel</h1>
          <p className="text-slate-400 text-sm mt-1">
            {isSuperAdmin ? 'Admin+ — Full control, audit log, practice management' : 'Manage user trial rewards'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isSuperAdmin && (
            <Button variant="outline" onClick={handleSendYearendEmails}
              className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/10 hidden sm:flex"
              data-testid="send-yearend-emails-btn">
              <Mail className="h-4 w-4 mr-2" />
              Send Year-End Emails
            </Button>
          )}
          {isSuperAdmin && (
            <Button onClick={() => setShowInviteForm(!showInviteForm)}
              className="bg-amber-600 hover:bg-amber-700 w-full sm:w-auto"
              data-testid="invite-toggle-btn">
              {showInviteForm ? <X className="h-4 w-4 mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
              {showInviteForm ? 'Cancel' : 'Invite Beta User'}
            </Button>
          )}
        </div>
      </div>

      {/* Tab Bar */}
      {isSuperAdmin && (
        <div className="flex gap-1 bg-slate-800 p-1 rounded-lg w-fit">
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'feedback', label: `Feedback${feedbackItems.filter(f => f.status === 'open').length > 0 ? ` (${feedbackItems.filter(f => f.status === 'open').length})` : ''}` },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.key ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700'
              }`} data-testid={`admin-tab-${tab.key}`}>
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {activeTab === 'overview' && (<>

      {/* Invite Form — Admin+ only */}
      {showInviteForm && isSuperAdmin && (
        <Card className="bg-slate-800 border-amber-500/30 border">
          <CardContent className="p-4 sm:p-6">
            <form onSubmit={handleInvite} className="space-y-4">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Mail className="h-4 w-4 text-amber-400" />
                Send Beta Invitation
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input type="email" placeholder="Email address" value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)} required
                  className="bg-slate-700 border-slate-600 text-white" data-testid="invite-email-input" />
                <Input type="text" placeholder="Name (optional)" value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white" data-testid="invite-name-input" />
              </div>
              <Button type="submit" disabled={submitting || !inviteEmail.trim()}
                className="bg-amber-600 hover:bg-amber-700" data-testid="send-invite-btn">
                <Plus className="h-4 w-4 mr-2" />
                {submitting ? 'Sending...' : 'Send Invite'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <AdminStatsRow realUsers={realUsers} pendingInvites={pendingInvites}
        activeTrialUsers={activeTrialUsers} subscribedUsers={subscribedUsers}
        expandedSection={expandedSection} setExpandedSection={setExpandedSection} />

      {/* Revenue Cards — Admin+ only */}
      {isSuperAdmin && revenue && (
        <AdminRevenue revenue={revenue} formatDate={formatDate} />
      )}

      <AdminExpandedSections expandedSection={expandedSection} pendingInvites={pendingInvites}
        activeTrialUsers={activeTrialUsers} subscribedUsers={subscribedUsers}
        isSuperAdmin={isSuperAdmin} canModifyUser={canModifyUser}
        onRevokeInvite={handleRevokeInvite} onExtend={startExtend} onDelete={setDeletingUser} />

      {/* Users List */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="pb-3 cursor-pointer select-none" onClick={() => toggleSection('users')}>
          <div className="flex items-center justify-between min-h-[44px]">
            <CardTitle className="text-white text-base sm:text-lg flex items-center gap-2">
              <Users className="h-4 w-4 text-amber-400" />
              Registered Users ({realUsers.length})
            </CardTitle>
            <div className="p-2 -m-2 rounded-md hover:bg-slate-700/50 transition-colors">
              {collapsedSections.users ? <ChevronDown className="h-5 w-5 text-slate-500" /> : <ChevronUp className="h-5 w-5 text-slate-400" />}
            </div>
          </div>
          {!collapsedSections.users && (
            <div className="mt-2" onClick={(e) => e.stopPropagation()}>
              <div className="relative">
                <Search className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input type="text" placeholder="Search by name or email..." value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-slate-700/50 border-slate-600 text-white pl-9 h-9 text-sm w-full sm:w-64"
                  data-testid="user-search-input" />
              </div>
            </div>
          )}
        </CardHeader>
        {!collapsedSections.users && (
        <CardContent className="p-0">
          <div className="divide-y divide-slate-700">
            {filteredUsers.length === 0 && (
              <p className="text-slate-500 text-sm text-center py-6">
                {searchQuery ? `No users matching "${searchQuery}"` : 'No users found'}
              </p>
            )}
            {filteredUsers.map((u) => (
              <AdminUserRow key={u.id} u={u} isSuperAdmin={isSuperAdmin} canModify={canModifyUser(u)}
                expandedUser={expandedUser} setExpandedUser={setExpandedUser}
                editingReferralSettings={editingReferralSettings} setEditingReferralSettings={setEditingReferralSettings}
                referralMaxInvites={referralMaxInvites} setReferralMaxInvites={setReferralMaxInvites}
                referralRewardDays={referralRewardDays} setReferralRewardDays={setReferralRewardDays}
                extendingUser={extendingUser} setExtendingUser={setExtendingUser}
                extendDays={extendDays} setExtendDays={setExtendDays}
                deletingUser={deletingUser} setDeletingUser={setDeletingUser}
                onUpdateUser={handleUpdateUser} onExtendTrial={handleExtendTrial}
                onSaveReferralSettings={handleSaveReferralSettings} onDeleteUser={handleDeleteUser} />
            ))}
          </div>
        </CardContent>
        )}
      </Card>

      {/* Audit Log — Admin+ only */}
      {isSuperAdmin && <AdminAuditLog />}

      {/* GDPR Break-Glass — Admin+ only */}
      {isSuperAdmin && <AdminBreakGlass />}

      {/* Accountant Practices Management — Admin+ only */}
      {isSuperAdmin && <AdminPractices />}

      </>)}

      {/* Feedback Tab */}
      {activeTab === 'feedback' && isSuperAdmin && (
        <AdminFeedback feedbackItems={feedbackItems} fetchData={fetchData} />
      )}

    </div>
  );
};

export default Admin;
